import React, { useRef, useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cssUrl } from '@/lib/cssUrl';

/**
 * The shelf rail — the marketplace's aisles, with artwork, sitting directly above the products.
 *
 * Eleven shelves do not fit on a desktop screen, so the rail scrolls with arrows either side.
 * A tile opens that shelf's own page: clicking something has to visibly go somewhere.
 */
export function ShelfRail({ shelves = [], active = 'all', onSelect }) {
  const withStock = shelves.filter((s) => (s.product_count || 0) > 0);
  const list = withStock.length ? withStock : shelves;
  const scrollRef = useRef(null);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);

  const measure = () => {
    const el = scrollRef.current;
    if (!el) return;
    setAtStart(el.scrollLeft <= 8);
    setAtEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 8);
  };

  useEffect(() => {
    const timer = setTimeout(measure, 150);
    const el = scrollRef.current;
    if (el) el.addEventListener('scroll', measure);
    window.addEventListener('resize', measure);
    return () => {
      clearTimeout(timer);
      if (el) el.removeEventListener('scroll', measure);
      window.removeEventListener('resize', measure);
    };
  }, [list.length]);

  const nudge = (dir) => {
    const el = scrollRef.current;
    if (el) el.scrollBy({ left: dir * 460, behavior: 'smooth' });
  };

  if (!list.length) return null;

  return (
    <div className="mp-shelf-rail-wrap">
      {!atStart && (
        <button type="button" className="mp-shelf-arrow left" onClick={() => nudge(-1)}
          aria-label="Previous shelves" data-testid="mp-shelf-scroll-left">
          <ChevronLeft size={18} />
        </button>
      )}

      <div className="mp-shelf-rail" ref={scrollRef} data-testid="mp-shelf-rail">
        <button
          type="button"
          className={`mp-shelf-tile all ${active === 'all' ? 'active' : ''}`}
          onClick={() => onSelect('all')}
          data-testid="mp-shelf-all"
        >
          <span className="mp-shelf-art all" />
          <span className="mp-shelf-label">All Products</span>
        </button>

        {list.map((s) => (
          <button
            type="button"
            key={s.id}
            className={`mp-shelf-tile ${active === s.slug ? 'active' : ''}`}
            onClick={() => onSelect(s.slug)}
            data-testid={`mp-shelf-${s.slug}`}
          >
            <span
              className="mp-shelf-art"
              style={s.artwork_url ? { backgroundImage: cssUrl(s.artwork_url) } : undefined}
            />
            <span className="mp-shelf-label">{s.name}</span>
            <span className="mp-shelf-count">{s.product_count || 0}</span>
          </button>
        ))}
      </div>

      {!atEnd && (
        <button type="button" className="mp-shelf-arrow right" onClick={() => nudge(1)}
          aria-label="More shelves" data-testid="mp-shelf-scroll-right">
          <ChevronRight size={18} />
        </button>
      )}
    </div>
  );
}

/** The same shelves, compact and labelled, for every marketplace page that is not the home. */
export function ShelfPills({ shelves = [], active = 'all', onSelect }) {
  if (!shelves.length) return null;
  return (
    <div className="mp-shelf-pills" data-testid="mp-shelf-pills">
      <span className="mp-shelf-pills-label">Shelves</span>
      <button
        type="button"
        className={`mp-shelf-pill ${active === 'all' ? 'active' : ''}`}
        onClick={() => onSelect('all')}
        data-testid="mp-shelf-pill-all"
      >
        All
      </button>
      {shelves.map((s) => (
        <button
          type="button"
          key={s.id}
          className={`mp-shelf-pill ${active === s.slug ? 'active' : ''}`}
          onClick={() => onSelect(s.slug)}
          data-testid={`mp-shelf-pill-${s.slug}`}
        >
          {s.name}
        </button>
      ))}
    </div>
  );
}

export default ShelfRail;
